import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/lib/api'
import {
  DEFAULT_PREFS,
  getCachedConfig,
  getPrefs,
  setCachedConfig,
  setPrefs,
  type DevicePrefs,
} from '@/lib/db'
import { report } from '@/lib/telemetry'
import type { ConfigResponse, WorkflowPublic } from '@/lib/api-types'

interface ConfigContextValue {
  workflows: WorkflowPublic[]
  /** True only while there is nothing at all to show. Cached data suppresses it. */
  loading: boolean
  /** Set when the last refresh failed; cached data may still be rendered. */
  staleError: string | null
  /** When the rendered config was fetched, so the UI can say "as of…". */
  fetchedAt: number | null
  prefs: DevicePrefs
  activeWorkflow: WorkflowPublic | null
  setActiveWorkflow: (id: string) => void
  updatePrefs: (patch: Partial<DevicePrefs>) => void
  refresh: () => Promise<void>
}

const ConfigContext = createContext<ConfigContextValue | null>(null)

/**
 * Loads workflow config, cache first.
 *
 * The rule this enforces: the UI never waits on the network to become usable.
 * Whatever was cached renders immediately, a refresh happens underneath, and a
 * failed refresh leaves the cached data on screen with a quiet warning rather
 * than replacing a working screen with an error.
 */
export function ConfigProvider({ children }: { children: ReactNode }) {
  const [workflows, setWorkflows] = useState<WorkflowPublic[]>([])
  const [fetchedAt, setFetchedAt] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [staleError, setStaleError] = useState<string | null>(null)
  const [prefs, setPrefsState] = useState<DevicePrefs>(DEFAULT_PREFS)

  const applyConfig = useCallback((config: ConfigResponse, at: number) => {
    setWorkflows(config.workflows)
    setFetchedAt(at)
  }, [])

  const refresh = useCallback(async () => {
    try {
      const config = await api.getConfig()
      applyConfig(config, Date.now())
      setStaleError(null)
      await setCachedConfig(config)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not refresh workflows.'
      setStaleError(message)
      report({ level: 'warn', event: 'config.refresh_failed', message })
    } finally {
      setLoading(false)
    }
  }, [applyConfig])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      const [cached, storedPrefs] = await Promise.all([getCachedConfig(), getPrefs()])
      if (cancelled) return

      setPrefsState(storedPrefs)

      if (cached !== null) {
        // Paint from cache before any request goes out.
        applyConfig(cached.config, cached.fetchedAt)
        setLoading(false)
      }

      await refresh()
    })()

    return () => {
      cancelled = true
    }
  }, [applyConfig, refresh])

  const updatePrefs = useCallback((patch: Partial<DevicePrefs>) => {
    // Optimistic: preferences are device-local, so there is nothing to reconcile
    // and no reason to make the user wait for a write.
    setPrefsState((current) => ({ ...current, ...patch }))
    void setPrefs(patch)
  }, [])

  const setActiveWorkflow = useCallback(
    (id: string) => updatePrefs({ activeWorkflowId: id }),
    [updatePrefs],
  )

  const activeWorkflow = useMemo(() => {
    if (workflows.length === 0) return null
    // Falling back to the first workflow matters: the saved one may have been
    // archived since this device last synced.
    return workflows.find((w) => w.id === prefs.activeWorkflowId) ?? workflows[0] ?? null
  }, [workflows, prefs.activeWorkflowId])

  const value = useMemo<ConfigContextValue>(
    () => ({
      workflows,
      loading,
      staleError,
      fetchedAt,
      prefs,
      activeWorkflow,
      setActiveWorkflow,
      updatePrefs,
      refresh,
    }),
    [
      workflows,
      loading,
      staleError,
      fetchedAt,
      prefs,
      activeWorkflow,
      setActiveWorkflow,
      updatePrefs,
      refresh,
    ],
  )

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export function useConfig(): ConfigContextValue {
  const ctx = useContext(ConfigContext)
  if (ctx === null) throw new Error('useConfig must be used inside <ConfigProvider>')
  return ctx
}
